
import { defineComponent } from 'vue'
import BasicCard from '@/components/cards/BasicCard.vue'
import { Store } from 'vuex'
import { Actions } from '@/store/enums/StoreEnums'
import { Atelier, CmsResponse } from '@/types/api-response'
import ApiService from '@/services/ApiService'
import Swal from 'sweetalert2'
import dateFormat from 'dateformat'
import { changeDateLocalization } from '@/config/i18n/date'
import { ElementAnimateUtil } from '@/assets/metronik/ts/_utils'

export default defineComponent({
  name: 'PortalAteliers',
  components: { BasicCard },
  data () {
    return {
      selectedDate: '' as string,
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      typeFilter: '' as string
    }
  },
  mounted () {
    ElementAnimateUtil.scrollTop(0, 500)

    if (this.$route.params.type) {
      this.typeFilter = (this.$route.params.type as string).replace(/-/g, '_')
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.$store.dispatch(Actions.FETCH_CMS_DATA)

    this.selectedDate = this.toDateStr(new Date())
  },
  methods: {
    toDateStr (d: Date): string {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    prevMonth () {
      if (this.currentMonth === 0) {
        this.currentMonth = 11
        this.currentYear--
      } else {
        this.currentMonth--
      }
    },
    nextMonth () {
      if (this.currentMonth === 11) {
        this.currentMonth = 0
        this.currentYear++
      } else {
        this.currentMonth++
      }
    },
    selectDate (dateStr: string) {
      this.selectedDate = dateStr
    },
    formatDateShort (date: string) {
      try {
        return dateFormat(date, 'dd/mm/yyyy')
      } catch {
        return date
      }
    },
    formatTime (date: string) {
      try {
        return dateFormat(date, 'HH:MM')
      } catch {
        return date
      }
    },
    getDuration (atelier: Atelier): string {
      if (!atelier.dateDebut || !atelier.dateFin) return '-'
      try {
        const start = new Date(atelier.dateDebut)
        const end = new Date(atelier.dateFin)
        const mins = Math.round((end.getTime() - start.getTime()) / 60000)
        if (mins >= 60) {
          const h = Math.floor(mins / 60)
          const m = mins % 60
          return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h} heure${h > 1 ? 's' : ''}`
        }
        return `${mins} minutes`
      } catch {
        return '-'
      }
    },
    getTypeLabel (type: string): string {
      const labels: Record<string, string> = {
        atelier_coaching: this.$t('views.portal.ateliers.types.coaching'),
        webinaire_alternance: this.$t('views.portal.ateliers.types.webinaire')
      }
      return labels[type] || type || ''
    },
    getModaliteLabel (modalite: string): string {
      const labels: Record<string, string> = {
        presentiel: this.$t('views.portal.ateliers.modalities.presentiel'),
        distanciel: this.$t('views.portal.ateliers.modalities.distanciel'),
        hybride: this.$t('views.portal.ateliers.modalities.hybride')
      }
      return labels[modalite] || modalite || ''
    },
    canRegisterToAtelier (atelier: Atelier): boolean {
      if (atelier.nbIns >= atelier.quota) return false
      if (!this.cmsData.ateliers?.canRegister) return false
      const deadline = new Date(atelier.dateDebut)
      deadline.setHours(deadline.getHours() - 48)
      return new Date() < deadline
    },
    registerAtelier (atelier: Atelier) {
      changeDateLocalization(this.$i18n.locale)
      const type = atelier.intitule || this.getTypeLabel(atelier.type)
      const date = this.formatDateShort(atelier.dateDebut)
      const start = this.formatTime(atelier.dateDebut)
      const end = this.formatTime(atelier.dateFin)
      const modalite = this.getModaliteLabel(atelier.modalite)

      Swal.fire({
        icon: 'info',
        title: this.$t('views.portal.ateliers.registerModal.title'),
        html: `${this.$t('views.portal.ateliers.registerModal.confirmation')}<br><br><strong>${type}</strong><br>${date} · ${start} – ${end}<br>${modalite}`,
        showCancelButton: true,
        confirmButtonText: this.$t('global.buttons.yes'),
        cancelButtonText: this.$t('global.buttons.cancel'),
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          ApiService.post<object, CmsResponse>('/admissibles/portal/registerAtelier/' + atelier.id, {}).then(async () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            await this.$store.dispatch(Actions.FETCH_CMS_DATA)
            Swal.fire({
              icon: 'success',
              title: this.$t('views.portal.ateliers.registerModal.success'),
              showConfirmButton: true
            })
          }).catch(() => {
            Swal.fire({
              icon: 'error',
              title: this.$t('views.portal.ateliers.registerModal.error'),
              showConfirmButton: true
            })
          })
        }
      })
    },
    unregisterAtelier (atelier: Atelier) {
      Swal.fire({
        text: this.$t('views.portal.ateliers.unregisterModal.confirmation'),
        width: '36em',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('global.buttons.yes'),
        cancelButtonText: this.$t('global.buttons.cancel'),
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          ApiService.post<object, CmsResponse>('/admissibles/portal/registerAtelier/' + atelier.id, {}).then(async () => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            await this.$store.dispatch(Actions.FETCH_CMS_DATA)
            Swal.fire({
              icon: 'success',
              title: this.$t('views.portal.ateliers.unregisterModal.success'),
              showConfirmButton: true
            })
          }).catch(() => {
            Swal.fire({
              icon: 'error',
              title: this.$t('views.portal.ateliers.unregisterModal.error'),
              showConfirmButton: true
            })
          })
        }
      })
    }
  },
  computed: {
    cmsData (): CmsResponse {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return (this.$store as Store<never>).getters.getCmsData
    },
    pageTitle (): string {
      if (this.typeFilter) {
        return this.getTypeLabel(this.typeFilter)
      }
      return this.$t('views.portal.ateliers.title')
    },
    allAteliers (): Atelier[] {
      let ateliers = this.cmsData.ateliers?.ateliers || []
      if (this.typeFilter) {
        ateliers = ateliers.filter(a => a.type === this.typeFilter)
      }
      return ateliers
    },
    registeredAteliers (): Atelier[] {
      return this.allAteliers.filter(a => Number(a.isInscrit) >= 1)
    },
    filteredAteliers (): Atelier[] {
      if (!this.selectedDate) return this.allAteliers.filter(a => !Number(a.isInscrit))
      return this.allAteliers.filter(a => {
        if (!a.dateDebut) return false
        const atelierDate = a.dateDebut.substring(0, 10)
        return atelierDate === this.selectedDate && !Number(a.isInscrit)
      })
    },
    atelierDatesMap (): Record<string, { hasAteliers: boolean; hasRegistered: boolean }> {
      const map: Record<string, { hasAteliers: boolean; hasRegistered: boolean }> = {}
      for (const a of this.allAteliers) {
        if (!a.dateDebut) continue
        const dateStr = a.dateDebut.substring(0, 10)
        if (!map[dateStr]) {
          map[dateStr] = { hasAteliers: false, hasRegistered: false }
        }
        map[dateStr].hasAteliers = true
        if (Number(a.isInscrit) >= 1) {
          map[dateStr].hasRegistered = true
        }
      }
      return map
    },
    weekDays (): string[] {
      const locale = this.$i18n.locale
      // Lundi = 1er jour (semaine ISO)
      return Array.from({ length: 7 }, (_, i) => {
        const day = new Date(2018, 0, 1 + i) // 2018-01-01 est un lundi
        return new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(day)
      })
    },
    currentMonthLabel (): string {
      const date = new Date(this.currentYear, this.currentMonth, 1)
      const locale = this.$i18n.locale
      const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date)
      return `${month.charAt(0).toUpperCase() + month.slice(1)} ${this.currentYear}`
    },
    calendarCells (): Array<{
      day: number | null
      dateStr: string
      isSelected: boolean
      hasAteliers: boolean
      hasRegistered: boolean
    }> {
      const cells = []
      const firstDay = new Date(this.currentYear, this.currentMonth, 1)
      let startDay = firstDay.getDay() - 1
      if (startDay < 0) startDay = 6

      for (let i = 0; i < startDay; i++) {
        cells.push({ day: null, dateStr: '', isSelected: false, hasAteliers: false, hasRegistered: false })
      }

      const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
        const info = this.atelierDatesMap[dateStr]
        cells.push({
          day: d,
          dateStr,
          isSelected: dateStr === this.selectedDate,
          hasAteliers: info?.hasAteliers || false,
          hasRegistered: info?.hasRegistered || false
        })
      }

      return cells
    }
  }
})
